import { useTranslation } from 'react-i18next';
import { User } from '@/design-system/icons';
import { NotificationContainer } from './NotificationContainer';
import type { NotificationItem, JoinRequestPayload } from '../types';
import { getNotificationState } from '../types';
import { useFormatRelativeTime } from '@/utils/timeUtils';
import { useEmployeeSidePanel } from '@/features/employee-sidepanel';

export interface JoinRequestNotificationProps {
  notification: NotificationItem;
  onClick?: () => void;
}

export function JoinRequestNotification({ notification, onClick }: JoinRequestNotificationProps) {
  const { t } = useTranslation();
  const { open } = useEmployeeSidePanel();
  const payload = notification.payload as unknown as JoinRequestPayload;
  const state = getNotificationState(notification);

  const timestamp = useFormatRelativeTime(notification.created_at);

  const handleClick = () => {
    const canOpenSidePanel = payload.company_id && payload.join_request_id;
    if (canOpenSidePanel) {
      open(
        {
          type: 'acceptJoinRequest',
          joinRequest: {
            id: payload.join_request_id!,
            userName: payload.username,
            userEmail: payload.email,
          },
        },
        payload.company_id!
      );
    }
    onClick?.();
  };

  return (
    <NotificationContainer
      icon={
        <div className="size-8 flex items-center justify-center rounded-full bg-primary/10">
          <User className="size-4 text-primary" />
        </div>
      }
      timestamp={timestamp}
      state={state}
      onClick={handleClick}
    >
      <p className="text-sm text-onsurface">
        <span className="font-medium">{payload.username || payload.email}</span>{' '}
        {t('notifications.joinRequest.message')}
      </p>
      {payload.username && payload.email && (
        <p className="text-xs text-muted truncate">{payload.email}</p>
      )}
    </NotificationContainer>
  );
}
